/**
 * Fetch the licensing path for whatever the learner has answered so far (#411).
 *
 * Re-queries on EVERY answer that can change the response (state, rung, stage),
 * because that is the whole promise of the step: the next question and the card
 * both change with the answer. Out-of-order responses are dropped by key, so a
 * slow Virginia answer can never overwrite a fast Michigan one.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchLicensingPath } from './licensingApi';
import type { LicenseRung, LicensingPath, ProcessStage } from './types';

export interface UseLicensingPathResult {
  path: LicensingPath | null;
  isLoading: boolean;
  /** True when the lookup itself failed (network / backend), NOT when the state has no data. */
  hasError: boolean;
}

export function useLicensingPath(
  jurisdiction: string | undefined,
  licenseRung?: LicenseRung,
  processStage?: ProcessStage
): UseLicensingPathResult {
  const [state, setState] = useState<UseLicensingPathResult>({
    path: null,
    isLoading: false,
    hasError: false,
  });
  const latestKey = useRef('');

  useEffect(() => {
    if (!jurisdiction) {
      latestKey.current = '';
      setState({ path: null, isLoading: false, hasError: false });
      return;
    }
    const key = [jurisdiction, licenseRung ?? '', processStage ?? ''].join('|');
    latestKey.current = key;
    setState((prev) => ({ ...prev, isLoading: true, hasError: false }));

    fetchLicensingPath({ jurisdiction, licenseRung, processStage })
      .then((path) => {
        if (latestKey.current !== key) return;
        setState({ path, isLoading: false, hasError: false });
      })
      .catch(() => {
        if (latestKey.current !== key) return;
        setState({ path: null, isLoading: false, hasError: true });
      });
  }, [jurisdiction, licenseRung, processStage]);

  return state;
}
